import { useState, useEffect, Suspense } from "react"
import { useRouter, useSearchParamsCompat } from "@/lib/router-helpers"
import { useTranslation } from "react-i18next"
import { useBookingStore } from "@/store/booking-store"
import { useAuth, User } from "@/components/auth-provider"
import BookingModeSelection from "@/components/booking-mode-selection"
import UserModal from "@/components/user-modal"
import BookingSummaryModal from "@/components/booking-summary-modal"
import { ArrowLeft } from "lucide-react"
import ProgressPill from "@/components/progress-pill"


export default function BookingModeContent() {
  const router = useRouter()
  const { t } = useTranslation()
  const [searchParams] = useSearchParamsCompat()
  const mode = (searchParams.get("mode") as "booking" | "reschedule") || "booking"
  const appointmentId = searchParams.get("appointmentId") || undefined
  
  const { 
    selectedBranch, 
    selectedService, 
    selectedDate, 
    selectedSlot,
    setBookingState
  } = useBookingStore()
  
  const { user } = useAuth()
  
  const [mounted, setMounted] = useState(false)
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [summaryOpen, setSummaryOpen] = useState(false)

  const [appointerUser, setAppointerUser] = useState<User>({
    id: "",
    mobile: "",
    firstname: "",
    lastname: "",
    email: "",
    active: false,
    created_at: new Date(),
    updated_at: new Date()
  })

  useEffect(() => {
    setMounted(true)
    // Don't check selectedSlot here to allow clearing it when navigating back
    if (!selectedDate || !selectedBranch || !selectedService) {
      router.push('/')
    }
  }, [selectedDate, selectedBranch, selectedService, router])

  function proceedToSummary() {
    const state = useBookingStore.getState()
    
    if (state.bookingType === "self") {
      if (user) {
        setAppointerUser(user)
      }
      setSummaryOpen(true)
    } else {
      setUserModalOpen(true)
    }
  }

  // Booking object for the modal
  const booking = {
    branch_id: selectedBranch?.id,
    branch_name: selectedBranch?.name,
    service_name: selectedService?.name,
    service_id: selectedService?.id,
    date: selectedDate || undefined,
    time_from: selectedSlot?.time_from,
    time_to: selectedSlot?.time_to,
    duration_id: selectedSlot?.duration_id
  }

  if (!mounted) return null

  return (
    <div className="w-full mx-auto md:px-4 md:pb-24 pt-2 font-sans">
      <div className="animate-fade-in-up">
       <div className="hidden md:block mb-6">
        <ProgressPill value={80} />
      </div>

      <div className="flex flex-col items-center mt-4 md:mt-8 mb-8 md:mb-0">
        <div className="w-full max-w-3xl rounded-3xl bg-white shadow-sm border border-gray-100 px-2 py-4 md:px-8 md:py-8 relative overflow-hidden">
           <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50 pointer-events-none" />
           <div className="relative z-10 w-full">
               <BookingModeSelection onContinue={proceedToSummary} />
           </div>
        </div>
      </div>

       <div className="flex justify-start mt-8 px-2 md:px-0">
         <button 
          onClick={() => {
            setBookingState({ selectedSlot: null })
            router.back()
          }} 
          className="group px-8 h-12 flex items-center gap-2 rounded-full border border-gray-200 bg-white text-gray-900 text-sm font-semibold hover:border-pink-500 hover:text-pink-600 transition-all duration-300 shadow-sm hover:shadow-md active:scale-95"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          {t("back")}
        </button>
      </div>
      </div>

      {/* USER INFO + OTP MODAL (for "other") */}
      {userModalOpen && (
        <UserModal 
            setUserModalOpen={setUserModalOpen} 
            setAppointerUser={setAppointerUser} 
            setSummaryOpen={setSummaryOpen} 
        />
      )}

      {/* BOOKING SUMMARY MODAL */}
      {summaryOpen && (
        <BookingSummaryModal 
          booking={booking} 
          appointerUser={appointerUser} 
          bookingTab={useBookingStore.getState().bookingType} 
          setSummaryOpen={setSummaryOpen} 
          mode={mode}
          appointmentId={appointmentId}
        />
      )}
    </div>
  )
}
